import { useState, useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { agencyRelationshipService } from './agencyRelationshipService';

export interface AgencyDashboardStats {
    totalTrainers: number;
    totalActiveClients: number;
    totalRelationships: number;
    avgClientsPerTrainer: number;
    isLoading: boolean;
    error: string | null;
}

export interface UseAgencyDashboardResult extends AgencyDashboardStats {
    refreshStats: () => Promise<void>;
}

export class AgencyDashboardService {
    async getDashboardStats(): Promise<AgencyDashboardStats> {
        try {
            const stats = await agencyRelationshipService.getTrainerStats();
            
            return {
                totalTrainers: stats.totalTrainers,
                totalActiveClients: stats.totalActiveClients,
                totalRelationships: stats.totalRelationships,
                avgClientsPerTrainer: stats.avgClientsPerTrainer,
                isLoading: false,
                error: null,
            };
        } catch (error: any) {
            throw new Error(error.message || 'Failed to fetch agency dashboard statistics');
        }
    }
}

export const agencyDashboardService = new AgencyDashboardService();

export const useAgencyDashboard = (): UseAgencyDashboardResult => {
    const [stats, setStats] = useState<AgencyDashboardStats>({
        totalTrainers: 0,
        totalActiveClients: 0,
        totalRelationships: 0,
        avgClientsPerTrainer: 0,
        isLoading: true,
        error: null,
    });

    const toast = useToast();

    const fetchStats = useCallback(async () => {
        setStats(prev => ({ ...prev, isLoading: true, error: null }));

        try {
            const dashboardStats = await agencyDashboardService.getDashboardStats();
            setStats({
                ...dashboardStats,
                isLoading: false,
                error: null,
            });
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to fetch agency dashboard statistics';
            setStats(prev => ({
                ...prev,
                isLoading: false,
                error: errorMessage,
            }));
            toast({
                title: 'Error',
                description: errorMessage,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    }, [toast]);

    const refreshStats = useCallback(async () => {
        await fetchStats();
    }, [fetchStats]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    return {
        ...stats,
        refreshStats,
    };
};
